import { defineStore } from 'pinia';
import { computed, ref } from 'vue';
import { io, Socket } from 'socket.io-client';
import { getAccessToken } from '@/services/api/client';

/**
 * Connection state for the UI indicator.
 *  - 'disconnected' → no session yet, or logged out
 *  - 'connecting'   → handshake in progress / reconnect attempt in flight
 *  - 'connected'    → live
 *  - 'offline'      → socket dropped and reconnection is being retried
 */
export type RealtimeStatus = 'disconnected' | 'connecting' | 'connected' | 'offline';

/**
 * Generic payload — the backend always enriches each event with `event` + `emittedAt`.
 */
export interface RealtimeEvent {
  event: string;
  emittedAt: string;
  [k: string]: unknown;
}

/**
 * Names the frontend listens for, mirrored from `backend/src/realtime/realtime.service.ts`.
 */
export const REALTIME_EVENT_NAMES = [
  'ticket:created',
  'ticket:updated',
  'ticket:status_changed',
  'ticket:assigned',
  'ticket:message',
  'ticket:worklog',
  'ticket:attachment',
  'notification:new',
] as const;
export type RealtimeEventName = (typeof REALTIME_EVENT_NAMES)[number];

type Handler = (payload: RealtimeEvent) => void;

export const useRealtimeStore = defineStore('realtime', () => {
  const status = ref<RealtimeStatus>('disconnected');
  const lastError = ref<string | null>(null);

  let socket: Socket | null = null;
  const handlers = new Map<RealtimeEventName, Set<Handler>>();

  const isConnected = computed(() => status.value === 'connected');

  function dispatch(event: RealtimeEventName, payload: RealtimeEvent) {
    const set = handlers.get(event);
    if (!set) return;
    for (const fn of set) {
      try {
        fn(payload);
      } catch (err) {
        console.error(`[realtime] handler error for ${event}`, err);
      }
    }
  }

  function connect() {
    if (socket && socket.connected) return;
    const token = getAccessToken();
    if (!token) return;

    // Vite dev server runs on a different port from the API. Use VITE_API_URL.
    const baseUrl = import.meta.env.VITE_API_URL || '';

    status.value = 'connecting';
    lastError.value = null;

    socket?.disconnect();
    socket = io(baseUrl, {
      path: '/socket.io',
      transports: ['websocket', 'polling'],
      auth: { token },
      withCredentials: true,
      reconnection: true,
      reconnectionAttempts: Infinity,
      reconnectionDelay: 1_000,
      reconnectionDelayMax: 8_000,
      timeout: 10_000,
    });

    socket.on('connect', () => {
      status.value = 'connected';
      lastError.value = null;
    });
    socket.on('disconnect', (reason) => {
      // 'io server disconnect' means the server closed the connection (e.g. auth failed); don't auto-reconnect there.
      if (reason === 'io server disconnect') {
        status.value = 'disconnected';
      } else {
        status.value = 'offline';
      }
    });
    socket.on('reconnect_attempt', () => {
      status.value = 'connecting';
    });
    socket.on('connect_error', (err) => {
      lastError.value = err?.message ?? 'Connection failed';
      // Stay in 'connecting' so the UI shows we're trying; status will flip to
      // 'connected' once it succeeds or stay in 'offline' otherwise.
    });

    for (const name of REALTIME_EVENT_NAMES) {
      socket.on(name, (payload: RealtimeEvent) => {
        dispatch(name, payload);
      });
    }
  }

  function disconnect() {
    if (!socket) return;
    socket.removeAllListeners();
    socket.disconnect();
    socket = null;
    status.value = 'disconnected';
  }

  /**
   * Auto-join the ticket-detail room so admin/customer/support viewing the same
   * ticket all receive granular updates.
   */
  function subscribeTicket(ticketId: string) {
    socket?.emit('subscribe-ticket', ticketId);
  }
  function unsubscribeTicket(ticketId: string) {
    socket?.emit('unsubscribe-ticket', ticketId);
  }

  /**
   * Register a listener for a specific event. Returns an unsubscribe function.
   */
  function on(event: RealtimeEventName, handler: Handler): () => void {
    if (!handlers.has(event)) handlers.set(event, new Set());
    handlers.get(event)!.add(handler);
    return () => handlers.get(event)?.delete(handler);
  }

  return {
    status,
    lastError,
    isConnected,
    connect,
    disconnect,
    subscribeTicket,
    unsubscribeTicket,
    on,
  };
});
